/** Fee category / item applicability (matches backend `applies_to`). */
export const FEE_APPLIES_TO = [
  { value: 'all', label: 'All students' },
  { value: 'resident', label: 'Resident' },
  { value: 'visiting', label: 'Visiting / day scholar' },
] as const;

export interface FeeCategoryScope {
  applies_to?: string | null;
  class_id?: string | null;
  class_ids?: string[] | null;
  program_id?: string | null;
  semester?: number | null;
  academic_year?: string | null;
}

export interface FeePlanScope {
  class_id?: string | null;
  class_ids?: string[] | null;
  program_id?: string | null;
}

/** Resolved class IDs for a category (empty = all classes). */
export function feeCategoryClassIds(category: FeeCategoryScope): string[] {
  if (Array.isArray(category.class_ids) && category.class_ids.length > 0) {
    return category.class_ids.map((id) => String(id).trim()).filter(Boolean);
  }
  const single = (category.class_id ?? '').toString().trim();
  return single ? [single] : [];
}

/** Resolved class IDs for a fee plan (empty = all classes). */
export function feePlanClassIds(plan: FeePlanScope): string[] {
  if (Array.isArray(plan.class_ids) && plan.class_ids.length > 0) {
    return plan.class_ids.map((id) => String(id).trim()).filter(Boolean);
  }
  const single = (plan.class_id ?? '').toString().trim();
  return single ? [single] : [];
}

/** Whether a plan is intended for the given class (empty plan scope = all classes). */
export function feePlanAppliesToClass(
  plan: FeePlanScope,
  studentClassId?: string | null
): boolean {
  const planClasses = feePlanClassIds(plan);
  if (planClasses.length === 0) return true;
  const classId = (studentClassId ?? '').toString().trim();
  if (!classId) return false;
  return planClasses.includes(classId);
}

/** Whether a category applies to the given class (empty category scope = all classes). */
export function feeCategoryAppliesToClass(
  category: FeeCategoryScope,
  studentClassId?: string | null
): boolean {
  const catClasses = feeCategoryClassIds(category);
  if (catClasses.length === 0) return true;
  const classId = (studentClassId ?? '').toString().trim();
  if (!classId) return false;
  return catClasses.includes(classId);
}

/** Bulk-invoice class scope from plan-linked categories. */
export function feePlanBulkClassScope(categories: FeeCategoryScope[]): {
  allowsAllClasses: boolean;
  classIds: string[];
} {
  if (categories.length === 0) {
    return { allowsAllClasses: false, classIds: [] };
  }
  const union = new Set<string>();
  let allowsAllClasses = false;
  for (const cat of categories) {
    const ids = feeCategoryClassIds(cat);
    if (ids.length === 0) {
      allowsAllClasses = true;
    } else {
      ids.forEach((id) => union.add(id));
    }
  }
  return {
    allowsAllClasses,
    classIds: Array.from(union),
  };
}

export interface FeeItemScopeSelection {
  applies_to: string;
  class_id?: string;
  program_id?: string;
  semester?: string;
  academic_year?: string;
}

function normalizeAppliesTo(value: string | null | undefined): string {
  const key = (value ?? 'all').trim().toLowerCase();
  if (key === 'regular' || key === 'day_scholar' || key === 'day-scholar') return 'visiting';
  if (key === 'resident' || key === 'visiting') return key;
  return 'all';
}

/** Category `all` allows any item scope; otherwise item must match category or be `all`. */
export function feeAppliesCompatible(
  categoryAppliesTo: string | null | undefined,
  itemAppliesTo: string | null | undefined
): boolean {
  const cat = normalizeAppliesTo(categoryAppliesTo);
  const item = normalizeAppliesTo(itemAppliesTo);
  return cat === 'all' || item === 'all' || cat === item;
}

function scopeIdCompatible(
  categoryValue: string | null | undefined,
  itemValue: string | null | undefined
): boolean {
  const cat = (categoryValue ?? '').trim();
  if (!cat) return true;
  const item = (itemValue ?? '').trim();
  if (!item) return true;
  return cat === item;
}

function scopeClassCompatible(
  category: FeeCategoryScope,
  itemClassId: string | null | undefined
): boolean {
  const catIds = feeCategoryClassIds(category);
  if (catIds.length === 0) return true;
  const item = (itemClassId ?? '').trim();
  if (!item) return true;
  return catIds.includes(item);
}

function scopeSemesterCompatible(
  categorySemester: number | null | undefined,
  itemSemesterRaw: string | null | undefined
): boolean {
  if (categorySemester == null) return true;
  const itemTrim = (itemSemesterRaw ?? '').trim();
  if (!itemTrim) return true;
  const itemSem = Number(itemTrim);
  if (!Number.isFinite(itemSem)) return true;
  return categorySemester === itemSem;
}

function scopeYearCompatible(
  categoryYear: string | null | undefined,
  itemYear: string | null | undefined
): boolean {
  const cat = (categoryYear ?? '').trim();
  if (!cat) return true;
  const item = (itemYear ?? '').trim();
  if (!item) return true;
  return cat === item;
}

/** Whether a fee category can be added to an item with the given scope fields. */
export function feeCategoryMatchesItemScope(
  category: FeeCategoryScope,
  item: FeeItemScopeSelection
): boolean {
  if (!feeAppliesCompatible(category.applies_to, item.applies_to)) return false;
  if (!scopeClassCompatible(category, item.class_id)) return false;
  if (!scopeIdCompatible(category.program_id, item.program_id)) return false;
  if (!scopeSemesterCompatible(category.semester, item.semester)) return false;
  if (!scopeYearCompatible(category.academic_year, item.academic_year)) return false;
  return true;
}

/** Student record types used for invoice line filtering. */
export const STUDENT_TYPE_OPTIONS = [
  { value: 'regular', label: 'Day scholar (regular)' },
  { value: 'resident', label: 'Resident (hostel)' },
  { value: 'visiting', label: 'Day scholar (visiting)' },
] as const;

const DAY_SCHOLAR_TYPE_VALUES = new Set<string>(
  STUDENT_TYPE_OPTIONS.filter((o) => o.value !== 'resident').map((o) => o.value)
);

function normalizeStudentTypeKey(type: string | null | undefined): string {
  return (type ?? '').trim().toLowerCase().replace(/-/g, '_');
}

export function studentTypeLabel(type: string | null | undefined): string {
  const key = normalizeStudentTypeKey(type);
  const found = STUDENT_TYPE_OPTIONS.find((o) => o.value === key);
  return found?.label ?? (type?.trim() || '—');
}

export function appliesToLabel(value: string | null | undefined): string {
  const key = (value ?? '').trim().toLowerCase();
  const found = FEE_APPLIES_TO.find((o) => o.value === key);
  return found?.label ?? (value?.trim() || '—');
}

/** Hostel / boarding student — not eligible for day-scholar transport. */
export function isResidentStudentType(type: string | null | undefined): boolean {
  const key = normalizeStudentTypeKey(type);
  return key === 'resident' || key === 'hostel' || key === 'boarding';
}

/** Day-scholar types from STUDENT_TYPE_OPTIONS (plus common aliases). */
export function isDayScholarStudentType(type: string | null | undefined): boolean {
  const key = normalizeStudentTypeKey(type);
  if (!key || isResidentStudentType(key)) return false;
  if (DAY_SCHOLAR_TYPE_VALUES.has(key)) return true;
  return key === 'day_scholar' || key === 'dayscholar';
}
